import ExcelJS from "exceljs";
// src/utils/grossSalesTransactionCsvUtils.js

/**
 * Convert an array of objects into a CSV string.
 * Uses the keys of the first object as headers.
 * @param {Object[]} objArray
 * @returns {string}
 */
export function convertToCSV(objArray) {
  if (!Array.isArray(objArray) || objArray.length === 0) return "";

  const headers = Object.keys(objArray[0]);
  const escape = (val) => {
    let str = val == null ? "" : String(val);
    if (str.includes('"')) str = str.replace(/"/g, '""');
    return `"${str}"`;
  };

  // Header row
  let csv = headers.map(escape).join(",") + "\r\n";

  // Data rows
  for (const row of objArray) {
    const line = headers.map((key) => escape(row[key])).join(",");
    csv += line + "\r\n";
  }

  return csv;
}

/**
 * Generate and download a CSV for Gross Sales vs Total Transactions per month.
 *
 * @param {Object} params
 * @param {{ labels: string[], datasets: Array<{ label: string, data: number[] }> }} params.chartData
 * @param {string} params.storess                - Store name (e.g. "All Stores" or specific)
 * @param {string} params.selectedMonth          - JSON stringified month range ({ $gte, $lte }) or "AllMonths"
 * @param {string} params.selectedYear           - Year (e.g. "2025")
 * @param {string} [params.fileName]             - Filename for download
 */
// export function exportGrossSalesTransactionsCSV({
//   chartData,
//   storess,
//   selectedMonth,
//   selectedYear,
//   fileName = "GrossSales-TotalTransactions.csv",
// }) {
//   if (!chartData || !chartData.labels || !chartData.datasets) {
//     console.error("exportGrossSalesTransactionsCSV: invalid chartData");
//     return;
//   }

//   const totalTxDataset = chartData.datasets.find(
//     (ds) => ds.label === "Total Transactions",
//   );
//   const growthSalesDataset = chartData.datasets.find(
//     (ds) => ds.label === "Gross Sales",
//   );
//   if (!totalTxDataset || !growthSalesDataset) {
//     console.error("exportGrossSalesTransactionsCSV: missing datasets");
//     return;
//   }

//   // Determine month label
//   let monthLabel = "All Months";
//   if (selectedMonth && selectedMonth !== "AllMonths") {
//     try {
//       const range = JSON.parse(selectedMonth);
//       const from = range.$lte?.split("T")[0] || "";
//       const to = range.$gte?.split("T")[0] || "";
//       monthLabel = `${selectedYear}-${from} ${selectedYear}-${to}`;
//     } catch (e) {
//       console.warn(
//         "exportGrossSalesTransactionsCSV: could not parse selectedMonth",
//         e,
//       );
//     }
//   }

//   // Build CSV data
//   const rows = chartData.labels.map((label, i) => ({
//     Brand: "Angels Pizza",
//     Store: storess || "All Stores",
//     Month: monthLabel,
//     TotalTransactions: totalTxDataset.data[i],
//     GrowthSales: growthSalesDataset.data[i],
//   }));

//   const csvString = convertToCSV(rows);
//   if (!csvString) {
//     console.warn("exportGrossSalesTransactionsCSV: no data to export");
//     return;
//   }

//   // Download
//   const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
//   const link = document.createElement("a");
//   link.href = URL.createObjectURL(blob);
//   link.download = fileName;
//   document.body.appendChild(link);
//   link.click();
//   document.body.removeChild(link);
// }
export async function exportGrossSalesTransactionsCSV({
  chartData,
  storess,
  fileName = "GrossSales-TotalTransactions.xlsx",
}) {
  if (!chartData) return;

  const round2 = (n) => {
    const x = Number(n);
    if (!Number.isFinite(x)) return 0;
    return Math.round(x * 100) / 100;
  };

  // ---- Build rows (uses comparison when available) ----
  const rows = [];

  const pushRows = (period, block) => {
    const labels = block?.labels || [];
    const grossSales = block?.grossSales || [];
    const txCounts = block?.txCounts || [];

    labels.forEach((label, i) => {
      const parts = String(label).split(" ");
      const monthName = parts[0] || "";
      const year = parts[1] || "";

      rows.push({
        Period: period,
        Month: monthName,
        Year: year,
        TotalTransactions: txCounts[i] ?? 0,
        GrossSales: round2(grossSales[i]),
      });
    });
  };

  if (
    chartData.comparison?.previousYearData &&
    chartData.comparison?.currentYearData
  ) {
    pushRows("Previous Year", chartData.comparison.previousYearData);
    pushRows("Current Year", chartData.comparison.currentYearData);
  } else {
    // fallback to datasets + labels
    const totalTxDataset = chartData.datasets?.find(
      (ds) => ds.label === "Total Transactions",
    );
    const grossSalesDataset = chartData.datasets?.find(
      (ds) => ds.label === "Gross Sales",
    );
    if (!chartData.labels || !totalTxDataset || !grossSalesDataset) return;

    chartData.labels.forEach((label, i) => {
      const parts = String(label).split(" ");
      const monthName = parts[0] || "";
      const year = parts[1] || "";

      rows.push({
        Period: "",
        Month: monthName,
        Year: year,
        TotalTransactions: totalTxDataset.data?.[i] ?? 0,
        GrossSales: round2(grossSalesDataset.data?.[i]),
      });
    });
  }

  if (!rows.length) return;

  // ---- Create workbook ----
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("Gross vs Transactions");

  // Colors (ARGB)
  const TITLE_BLUE = "FF0076BA";
  const HEADER_GREEN = "FF83C14A";
  const LIGHT_GRAY = "FFF2F2F2";
  const BORDER = "FFCCCCCC";

  const borderStyle = {
    top: { style: "thin", color: { argb: BORDER } },
    left: { style: "thin", color: { argb: BORDER } },
    bottom: { style: "thin", color: { argb: BORDER } },
    right: { style: "thin", color: { argb: BORDER } },
  };

  // ---- Title ----
  ws.mergeCells("A1:E1");
  ws.getCell("A1").value = "Gross Sales vs. Total Transactions";
  ws.getCell("A1").font = { bold: true, size: 14, color: { argb: "FFFFFFFF" } };
  ws.getCell("A1").alignment = { horizontal: "center", vertical: "middle" };
  ws.getCell("A1").fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: TITLE_BLUE },
  };
  ws.getRow(1).height = 24;

  // ---- Meta (Brand/Store) ----
  ws.getCell("A2").value = "Brand";
  ws.getCell("B2").value = "Angels Pizza";
  ws.getCell("A3").value = "Store";
  ws.getCell("B3").value = storess || "All Stores";

  ["A2", "A3"].forEach((addr) => {
    ws.getCell(addr).font = { bold: true };
    ws.getCell(addr).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: LIGHT_GRAY },
    };
  });

  // ---- Table header ----
  const headerRow = 5;
  ws.getRow(headerRow).values = [
    "Period",
    "Month",
    "Year",
    "Total Transactions",
    "Gross Sales",
  ];
  ws.getRow(headerRow).font = { bold: true, color: { argb: "FFFFFFFF" } };
  ws.getRow(headerRow).alignment = { horizontal: "center", vertical: "middle" };
  ws.getRow(headerRow).height = 18;

  ws.getRow(headerRow).eachCell((cell) => {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: HEADER_GREEN },
    };
    cell.border = borderStyle;
  });

  // ---- Data rows ----
  let r = headerRow + 1;
  rows.forEach((row) => {
    ws.getRow(r).values = [
      row.Period,
      row.Month,
      row.Year,
      Number(row.TotalTransactions || 0),
      Number(row.GrossSales || 0),
    ];

    // Formats
    ws.getCell(`D${r}`).numFmt = "#,##0";
    ws.getCell(`E${r}`).numFmt = "#,##0.00";

    ws.getRow(r).eachCell((cell, col) => {
      cell.border = borderStyle;
      cell.alignment =
        col === 4 || col === 5
          ? { horizontal: "right", vertical: "middle" }
          : { horizontal: "left", vertical: "middle" };
    });

    r++;
  });

  // ---- Column widths ----
  ws.getColumn(1).width = 16; // Period
  ws.getColumn(2).width = 14; // Month
  ws.getColumn(3).width = 8; // Year
  ws.getColumn(4).width = 20; // Total Transactions
  ws.getColumn(5).width = 16; // Gross Sales

  // Freeze + filter
  ws.views = [{ state: "frozen", ySplit: headerRow }];
  ws.autoFilter = {
    from: { row: headerRow, column: 1 },
    to: { row: headerRow, column: 5 },
  };

  // ---- Download (no file-saver) ----
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
